import React, { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 1_500_000; // ~1.5 MB raw input; we'll compress below 800KB

async function compressToDataUrl(file, maxDim = 512, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AvatarUpload({ value, onChange, name = "", size = "lg", testid = "avatar-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const sizeCls = size === "sm" ? "h-16 w-16" : size === "md" ? "h-20 w-20" : "h-28 w-28";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be under 1.5 MB before compression");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await compressToDataUrl(file);
      onChange(dataUrl);
    } catch {
      toast.error("Could not read image");
    } finally {
      setBusy(false);
    }
  };

  const initial = (name || "U").trim().charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div className={`${sizeCls} relative rounded-full overflow-hidden bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center`}>
        {value ? (
          <img src={value} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <span className="font-display font-black text-3xl text-[#4B5563]">{initial}</span>
        )}
        {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><div className="h-5 w-5 rounded-full border-2 border-[#111827] border-t-transparent animate-spin" /></div>}
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-pick-button`}
          className="inline-flex items-center gap-2 rounded-md border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-bold text-[#111827] hover:border-[#111827] transition-colors"
        >
          <Camera size={14} /> {value ? "Change photo" : "Upload photo"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testid}-remove-button`}
            className="inline-flex items-center gap-2 text-xs font-bold text-[#4B5563] hover:text-[#EF4444] transition-colors"
          >
            <X size={12} /> Remove
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}
